import { useMemo, useState, type FormEvent } from 'react';

import { IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { ApiError } from '../../lib/api-client';
import { APP_TIMEZONE } from '../../lib/datetime';
import { exportRowsToExcel } from '../../lib/excel-export';
import { ReportAnalyticsGrid, ReportAreaChartCard, ReportBarChartCard, type ReportChartDatum } from './ReportAnalytics';
import { reportChartColor } from './report-chart-colors';
import { useRevenueReport } from './reports.queries';
import type { ReportSection, RevenueReportRow } from './types';

interface RevenueFilters {
  fromDate: string;
  toDate: string;
}

const columns: { key: ReportSection; label: string }[] = [
  { key: 'opd', label: 'OPD' },
  { key: 'emg', label: 'EMG' },
  { key: 'ipd', label: 'IPD' },
  { key: 'daycare', label: 'Daycare' },
  { key: 'dialysis', label: 'Dialysis' },
  { key: 'ot', label: 'OT' },
  { key: 'investigation', label: 'Investigation' },
];

const isoToday = () => new Date().toISOString().slice(0, 10);
const initialFilters = (): RevenueFilters => ({ fromDate: isoToday(), toDate: isoToday() });
const emptyValues = (): RevenueReportRow['values'] => Object.fromEntries(columns.map((column) => [column.key, { amount: 0, count: 0 }])) as RevenueReportRow['values'];
const formatDate = (value: string) => new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: APP_TIMEZONE }).format(new Date(`${value}T00:00:00`));
const formatAmount = (value: number) => value.toLocaleString('en-IN', { maximumFractionDigits: 2 });

const RevenueCell = ({ amount, count }: { amount: number; count: number }) => (
  <>
    {formatAmount(amount)} <span>({count.toLocaleString('en-IN')})</span>
  </>
);

export const RevenueReportPage = () => {
  const [draft, setDraft] = useState<RevenueFilters>(initialFilters);
  const [filters, setFilters] = useState<RevenueFilters>(draft);
  const query = useRevenueReport(filters);

  const rows = useMemo(() => query.data ?? [], [query.data]);
  const totals = useMemo(() => rows.reduce((summary, row) => {
    columns.forEach((column) => {
      summary[column.key].amount += row.values[column.key].amount;
      summary[column.key].count += row.values[column.key].count;
    });
    return summary;
  }, emptyValues()), [rows]);

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters(draft);
  };

  const reset = () => {
    const next = initialFilters();
    setDraft(next);
    setFilters(next);
  };

  const totalForRow = (row: RevenueReportRow) => columns.reduce((sum, column) => ({
    amount: sum.amount + row.values[column.key].amount,
    count: sum.count + row.values[column.key].count,
  }), { amount: 0, count: 0 });
  const grandTotal = columns.reduce((sum, column) => ({
    amount: sum.amount + totals[column.key].amount,
    count: sum.count + totals[column.key].count,
  }), { amount: 0, count: 0 });
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Revenue report could not be loaded.';
  const sectionChartData = useMemo<ReportChartDatum[]>(() => columns.map((column, index) => ({
    name: column.label,
    Amount: totals[column.key].amount,
    Bills: totals[column.key].count,
    color: reportChartColor(index),
  })), [totals]);
  const trendChartData = useMemo<ReportChartDatum[]>(() => rows.map((row) => ({
    name: row.date,
    Total: columns.reduce((sum, column) => sum + row.values[column.key].amount, 0),
    OPD: row.values.opd.amount,
    EMG: row.values.emg.amount,
    IPD: row.values.ipd.amount,
    Daycare: row.values.daycare.amount,
    Dialysis: row.values.dialysis.amount,
    Investigation: row.values.investigation.amount,
  })), [rows]);

  const exportToExcel = async () => {
    await exportRowsToExcel({
      fileName: `Revenue-Report-${filters.fromDate}-to-${filters.toDate}`,
      title: 'Total Revenue Report',
      subtitle: `${filters.fromDate} to ${filters.toDate}`,
      columns: [
        { header: 'Date', width: 14, map: (row: RevenueReportRow) => row.date },
        ...columns.map((column) => ({ header: column.label, width: 16, align: 'right' as const, numFmt: '#,##0.00', map: (row: RevenueReportRow) => row.values[column.key].amount })),
        { header: 'Total', width: 16, align: 'right', numFmt: '#,##0.00', map: (row: RevenueReportRow) => totalForRow(row).amount },
      ],
      rows,
    });
  };

  return (
    <div className="page-stack revenue-report-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Reports</p>
          <h1>Total Revenue Report</h1>
          <p>Date-wise revenue totals across OPD, EMG, IPD, Daycare, Dialysis, and Investigation.</p>
        </div>
      </div>

      <form className="revenue-report-filter-card" onSubmit={applyFilters}>
        <input type="date" value={draft.fromDate} max={draft.toDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} aria-label="From date" />
        <input type="date" value={draft.toDate} min={draft.fromDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} aria-label="To date" />
        <button className="button button-primary revenue-report-action" type="submit"><IconSearch />Search</button>
        <button className="button button-secondary revenue-report-action" type="button" onClick={reset}><IconRefreshCw />Reset</button>
        <ExcelExportButton className="revenue-report-action" onExport={exportToExcel} disabled={!query.isSuccess || rows.length === 0} label="Excel" />
      </form>

      {query.isSuccess ? (
        <ReportAnalyticsGrid>
          <ReportBarChartCard
            eyebrow="Section analysis"
            title="Revenue by section"
            data={sectionChartData}
            bars={[{ key: 'Amount', name: 'Amount', color: '#0d6b66' }]}
            valueKind="amount"
          />
          <ReportAreaChartCard
            eyebrow="Date trend"
            title="Daily revenue movement"
            data={trendChartData}
            areas={[
              { key: 'Total', name: 'Total', color: '#0d6b66' },
              { key: 'OPD', name: 'OPD', color: '#735ca5' },
              { key: 'Investigation', name: 'Investigation', color: '#1b8298' },
            ]}
            valueKind="amount"
          />
        </ReportAnalyticsGrid>
      ) : null}

      <section className="content-card revenue-report-card">
        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load revenue report" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && rows.length === 0 ? <EmptyState title="No revenue found" message="Bills in the selected date range will appear here." /> : null}
        {query.isSuccess && rows.length > 0 ? (
          <div className="revenue-report-table-scroll">
            <table className="revenue-report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {columns.map((column) => <th key={column.key}>{column.label}</th>)}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const rowTotal = totalForRow(row);
                  return (
                    <tr key={row.date}>
                      <td className="date-cell">{formatDate(row.date)}</td>
                      {columns.map((column) => (
                        <td key={column.key}><RevenueCell {...row.values[column.key]} /></td>
                      ))}
                      <td><RevenueCell {...rowTotal} /></td>
                    </tr>
                  );
                })}
                <tr className="revenue-report-total-row">
                  <td>Total</td>
                  {columns.map((column) => (
                    <td key={column.key}><RevenueCell {...totals[column.key]} /></td>
                  ))}
                  <td><RevenueCell {...grandTotal} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
};
